/*
 * Authors: mariushirjanu
 * Created: 27/03/2023
 */
import _ from 'underscore';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, Type, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { BILLINGTYPE_CENTRAL } from '@app/shared/services/billing/billing-data.service';
import { OrderEntryWebshopService } from '../../webshop/order-entry-webshop.service';
import { ICountry, ILocationExtended, IObject } from '@type_definitions/datatypes';
import { OrderEntryLocationService } from '../order-entry-location.service';
import { IInvoiceAddress, InvoiceAddressType, IShippingAddress, LocationDataType, OrderEntryAddressType, OrderEntryAddressTypeExtra, OrderEntryLocationDataService, ShippingAddressType } from '../order-entry-location-data.service';
import { OrderEntryShippingService } from '../shipping/order-entry-shipping.service';
import { OrderEntryRequesterService } from '../../requester/order-entry-requester.service';
import { OrderEntryInvoiceService } from '../invoice/order-entry-invoice.service';
import { OrderLocationAddressCurrentComponent } from './order-location-address-current.component';
import { OrderLocationAddressExistingComponent } from './order-location-address-existing.component';
import { OrderLocationAddressRecentComponent } from './order-location-address-recent.component';
import { IAddressData, IAddressOption } from '../base-order-entry-location-to.service';
import { GaValidateService, IValidatingComponent, IValidationContext } from '@app/shared/formly/ga-validate.service';
import { DynamicComponent } from 'ng-dynamic-component';
import { BillingType } from '../../order-entry-data.service';
import { WIZARD_ORDER } from '@app/shared/app-wizards';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { BaseLocationAddressComponent } from '@app/main/order/entry/location/address/base-location-address.component';

type ILocationAddressSelector = OrderLocationAddressCurrentComponent|OrderLocationAddressExistingComponent|OrderLocationAddressRecentComponent;

interface IAddressTypeModel {
  type:IAddressOption;
}

@Component({
  selector: 'order-location-address',
  template: `
    <div class="address" [hidden]="loading">
      <h2 translate>{{ title }}</h2>
      <div class="row">
        <div class="col-xsm-6 col-sm-4 col-md-3">
          <formly [form]="addressTypeForm" [fields]="addressTypeFields" [model]="addressTypeModel" class="form-horizontal"></formly>
        </div>
        <div class="col-xsm-6 col-sm-8 col-md-9">
          <div class="address-form">
            <ndc-dynamic [ndcDynamicComponent]="selectedAddressComponentName" [ndcDynamicInputs]="selectedAddressComponentInputs" [ndcDynamicOutputs]="selectedAddressComponentOutputs"></ndc-dynamic>
          </div>
        </div>
      </div>
    </div>
  `
})
export class OrderLocationAddressComponent implements IValidatingComponent, OnInit, OnChanges {
  @ViewChild(DynamicComponent) selectedAddressComponent:DynamicComponent;

  @Input() public title:string;
  @Input() public locationToModel:OrderEntryShippingService|OrderEntryInvoiceService;
  @Input() public otherLocationToModel:OrderEntryShippingService|OrderEntryInvoiceService;
  @Input() public dataType:LocationDataType;
  @Input() public fixedList:boolean;
  @Input() public billingType:BillingType;
  @Output() public onLoadAddressDone:EventEmitter<void> = new EventEmitter<void>();
  @Output() public onAddressTypeChange:EventEmitter<InvoiceAddressType | ShippingAddressType> = new EventEmitter<InvoiceAddressType | ShippingAddressType>();
  @Output() public onLocationChange:EventEmitter<ILocationExtended> = new EventEmitter<ILocationExtended>();

  public addressTypeForm:UntypedFormGroup = new UntypedFormGroup({});
  public addressTypeModel:IAddressTypeModel = <any>{};
  public addressType:InvoiceAddressType | ShippingAddressType;
  public addressTypeFields:IExtraFieldConfigurationObject[];
  public loading:boolean;

  public selectedAddressComponentName:Type<ILocationAddressSelector>;
  public selectedAddressComponentInputs:Partial<BaseLocationAddressComponent> = {};
  public selectedAddressComponentOutputs:IObject;
  private addressTypeField:IExtraFieldConfigurationObject;
  private webshopCountry:ICountry;

  constructor(
    private formlyService:GaFormlyService,
    private orderEntryLocationService:OrderEntryLocationService,
    private orderEntryLocationDataService:OrderEntryLocationDataService,
    private orderEntryRequesterService:OrderEntryRequesterService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private orderEntryInvoiceService:OrderEntryInvoiceService,
    private validateService:GaValidateService,
    private wizardService:GaWizardService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.webshopCountry = this.orderEntryWebshopService.selectedCountry;
    this.createFields();
    this.fillFields();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.billingType) {
      this.billingType = changes.billingType.currentValue;
      if (changes.billingType && !changes.billingType.firstChange) { // ignore the first change because it is not triggered by user
        this.onBillingTypeChange();
      }
    }
  }

  private fillFields():void {
    const requesterId:number = this.orderEntryRequesterService.contact?.id;
    const selectedShopId:number = this.orderEntryWebshopService.selectedShop?.id;
    const selectedCountryId:number = this.webshopCountry?.id;
    const billingType:string = this.orderEntryInvoiceService.billingType?.id || this.orderEntryWebshopService.shopContext?.billingType;
    const testMode:boolean = this.orderEntryWebshopService.testMode;
    const bpId:number = this.orderEntryWebshopService.selectedBP?.id;

    // perform load only when webshop, country, bp, and requester are selected
    if (selectedShopId && selectedCountryId) {
      this.loading = true;
      this.formlyService.fillField(this.addressTypeField, this.orderEntryLocationDataService.addresses(this.dataType, requesterId, selectedShopId, selectedCountryId, testMode, bpId, billingType)
        .then((address:IInvoiceAddress[]|IShippingAddress[]) => {
          // !!! setAddressTypeOptions also updates address data to model
          this.locationToModel.setAddressTypeOptions(address, true);
          this.preselectAddressType();
          this.initAddressModels();
          return this.locationToModel.addressTypeOptions;
        })
        .finally(() => {
          this.loading = false;
          this.onLoadAddressDone.emit();
        }));
    }
  }

  private preselectAddressType():void {
    // load data from locationToModel
    let selectedAddressType:IAddressOption = _.findWhere(this.locationToModel.addressTypeOptions, { id: this.selectedAddressType?.id });
    if (!selectedAddressType) {
      // when there is no address type or the address type is set to CURRENT_ADDRESS -> try to select a default location
      selectedAddressType = _.findWhere(this.locationToModel.addressTypeOptions, { default: true });
    }

    this.selectedAddressType = selectedAddressType;
    this.addressTypeField.formControl.setValue(selectedAddressType);
  }

  private initAddressModels = ():void => {
    // when selected address has been already set, do not fetch it again from address object (server)
    // this is only the case when initialising for the first time when visiting Shipping/Invoice tab
    if(_.isEmpty(this.selectedAddress)) {
      _.each(this.orderEntryLocationDataService.LOC_TYPES, (typeId:InvoiceAddressType | ShippingAddressType) => {
        let addressModel:IAddressData = <IAddressData>{};
        const addressData:IAddressData = this.locationToModel.addressData;
        const loadedAddress:IShippingAddress|IInvoiceAddress = this.locationToModel.getLoadedAddress(typeId);

        switch (typeId) {
          case OrderEntryAddressType.CURRENT_ADDRESS:
            addressModel.location = addressData?.location; // loaded location
            break;
          case OrderEntryAddressTypeExtra.SAME_AS_SHIPPING_ADDRESS:
            addressModel = _.clone(this.otherLocationToModel.addressData) || <IAddressData>{};
            if(!_.isEmpty(this.otherLocationToModel.selectedAddress)){
              addressModel = _.clone(this.otherLocationToModel.selectedAddress);
            }
            break;
          case OrderEntryAddressTypeExtra.OFFICE_ADDRESS:
            if (loadedAddress) {
              addressModel.location = loadedAddress.location;
              addressModel.vatName = loadedAddress.vat?.name;
              addressModel.office = (<IShippingAddress>loadedAddress).office;
            }
            break;
          case OrderEntryAddressTypeExtra.DEFAULT_SHIPPING_ADDRESS:
          case OrderEntryAddressTypeExtra.DEFAULT_INVOICE_ADDRESS:
            if (loadedAddress) {
              addressModel.location = loadedAddress.location;
              addressModel.vatName = loadedAddress.vat?.name;
              addressModel.office = (<IShippingAddress>loadedAddress).office;
              addressModel.defaultAddress = loadedAddress.default || true;
            }
            break;
          case OrderEntryAddressType.EXISTING_LOCATION:
            addressModel.searchCountry = _.clone(addressData?.country || loadedAddress?.location?.companyCountry?.country || this.webshopCountry);
            break;
          case OrderEntryAddressType.RECENT_ADDRESS:
            //no need to add anything to the model, lists will be loaded when needed
            break;
          case OrderEntryAddressType.MANUAL_ADDRESS:
            addressModel.country = this.orderEntryWebshopService.selectedCountry; //initialize to selected country in context
            break;
          default:
            break;
        }

        this.locationToModel.setAddress(typeId, addressModel);
        this.orderEntryLocationService.updateLocationData(addressModel);
      });
    }
  };

  private createFields():void {
    this.addressTypeFields = [
      this.addressTypeField = this.formlyService.aRadio('type', 'ORDERENTRY.LOCATION.ADDRESS_TYPE')
        .isRequired()
        .withFormat('type')
        .withoutWrapper()
        .onChange(this.onAddressTypeFieldChange)
        .build()
    ];
  }

  private onAddressTypeFieldChange = (type?:IAddressOption) => {
    type = type || this.addressTypeModel.type;

    this.addressType = type?.id;
    const addressType:ShippingAddressType | InvoiceAddressType = this.addressType;

    if (addressType) {
      this.selectedAddressType = this.addressTypeModel.type;
      this.selectAddressForm(addressType);
      this.onAddressTypeChange.emit(addressType);
    }
  };

  private selectAddressForm(typeId:InvoiceAddressType | ShippingAddressType):void {
    if (typeId) {
      this.selectedAddress = this.locationToModel.getAddress(typeId);

      // common inputs for all address components
      this.selectedAddressComponentInputs = {
        dataType: this.dataType,
        fixedList: this.fixedList,
        typeId,
        addressType: typeId,
        locationToModel: this.locationToModel,
        addEditAllowed: this.allowAddEditLocation()
      };

      switch (typeId) {
        case OrderEntryAddressTypeExtra.DEFAULT_SHIPPING_ADDRESS:
        case OrderEntryAddressTypeExtra.DEFAULT_INVOICE_ADDRESS:
        case OrderEntryAddressTypeExtra.SAME_AS_SHIPPING_ADDRESS:
        case OrderEntryAddressType.CURRENT_ADDRESS:
        case OrderEntryAddressTypeExtra.OFFICE_ADDRESS:
        case OrderEntryAddressType.MANUAL_ADDRESS:
          this.selectedAddressComponentName = OrderLocationAddressCurrentComponent;
        break;
        case OrderEntryAddressType.EXISTING_LOCATION:
          this.selectedAddressComponentInputs.isAsyncLocationData = true;
          this.selectedAddressComponentOutputs = {
            onLocationChangeEmit: this.onLocationChangeEmit
          };
          this.selectedAddressComponentName = OrderLocationAddressExistingComponent;
        break;
        case OrderEntryAddressType.RECENT_ADDRESS:
          this.selectedAddressComponentInputs.isAsyncLocationData = true;
          this.selectedAddressComponentName = OrderLocationAddressRecentComponent;
        break;
      }
    }
  }

  private allowAddEditLocation():boolean {
    return !this.fixedList && !(this.dataType === LocationDataType.INVOICE && this.billingType === BILLINGTYPE_CENTRAL);
  }

  private onBillingTypeChange():void {
    const addressTypeOptions:IAddressOption[] = this.locationToModel.addressTypeOptions;
    this.formlyService.fillField(this.addressTypeField, addressTypeOptions)
      .then(() => {
        // always preselect the default option 'Default invoice address' for the invoice address when Billing type changes (if available)
        this.preselectAddressType();

        // initialise the address model
        this.initAddressModels();

        // this makes sure to trigger a new onChange for address type field, even if the field is not changed in order to propagate changes to order-location-component (via onSelectedAddressChange.emit)
        this.onAddressTypeFieldChange();
      });
  }

  public onLocationChangeEmit = (selectedLocation:ILocationExtended):void => {
    this.onLocationChange.emit(selectedLocation);
  };

  public beforeValidate = ():Promise<any> => {
    // make sure that the dynamic subcomponent for the address is already instantiated
    return this.selectedAddressComponent?.componentRef?.instance.beforeValidate();
  };

  public validate(context?:IValidationContext):Promise<void|string[]> {
    // merge validation errors from the current component with the validation errors from the selected subcomponent
    let validateSelectedAddressComponent:Promise<void|string[]>;
    if (this.selectedAddressComponent?.componentRef?.instance) {
      validateSelectedAddressComponent = this.selectedAddressComponent.componentRef.instance.validate(context);
    } else {
      validateSelectedAddressComponent = this.validateService.validateComponents(
        [ { class: this.selectedAddressComponentName } ],
        this.wizardService.getInstance(WIZARD_ORDER).shadowValidationTemplate,
        context,
        this.selectedAddressComponentInputs)
        .catch(results => Promise.reject(results.allErrors))
        .then(); // empty to satisfy the typescript compiler
    }
    return this.validateService.validateSubcomponents([
      this.validateService.validateFields(this.addressTypeFields, this.addressTypeModel, true),
      validateSelectedAddressComponent
    ]);
  }

  // define getter & setter for selected address type
  public get selectedAddressType():IAddressOption {
    return this.locationToModel.selectedAddressType;
  }
  public set selectedAddressType(addressType:IAddressOption) {
    this.locationToModel.selectedAddressType = addressType;
  }

  // define getter & setter for selected address
  public get selectedAddress():IAddressData {
    return this.locationToModel.selectedAddress;
  }
  public set selectedAddress(selectedAddress:IAddressData) {
    this.locationToModel.selectedAddress = selectedAddress;
    this.selectedAddressComponentInputs.addressModel = selectedAddress;
  }

  public get address():IAddressData {
    return this.locationToModel.addressData;
  }
}